import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Modal from "../../layouts/Modal";
import useEbay from "../../data/useEbay";
import usePartDetails from "../../data/usePartDetails";
import { IPart } from "../../models/Part";
import SelectInput from "../shared/Inputs/SelectInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import useNotification from "../notifications/useNotifications";
import PriceFormat from "../../utils/priceFormat";
import { FormatType } from "../../models/EbayOffer";
import EbayListingTitle, { computeEbayListingTitle } from "../ebayListings/EbayListingTitle";
import LoadingPulse from "../shared/LoadingPulse";
import {
  IListingOptimisation,
  IStageListingOptimisationItem,
} from "../../models/listingOptimisation/IListingOptimisation";

interface IStageOptimisationFormFields {
  newTitle: string;
  newPrice: string;
  newFulfillmentPolicyId: string;
  newPaymentPolicyId: string;
  newReturnPolicyId: string;
  newLocationKey: string;
  applyImageRules: boolean;
  removeBackgroundFirstImage: boolean;
  applyCategoryDefaults: boolean;
}

interface IStageOptimisationModalProps {
  open: boolean;
  onClose: () => void;
  part: IPart | null;
  optimisation: IListingOptimisation | undefined;
  onSubmit: (item: IStageListingOptimisationItem) => void;
  isLoading: boolean;
}

// Edits the staged (not live) values for a single listing. Staged values are only
// applied when the listing is sent to be relisted - nothing here touches eBay.
export default function StageOptimisationModal({
  open,
  onClose,
  part,
  optimisation,
  onSubmit,
  isLoading,
}: IStageOptimisationModalProps) {
  const { addNotification } = useNotification();
  const { policies, locations } = useEbay();
  // Bumped when applying the Wilma default so EbayListingTitle remounts with the new form value.
  const [titleEditorKey, setTitleEditorKey] = useState(0);

  // Listings rows are slim - fetch full part details so the title builder has
  // vehicle/aspect values for merge tags (same pattern as UnlistedEbayTitle).
  const { partDetails, isLoading: detailsLoading } = usePartDetails(part?.id ?? "", open && !!part);
  const fullPart = partDetails ?? part;

  const offer = fullPart?.offers?.at(0) ?? part?.offers?.at(0);
  const currentPrice =
    offer?.format === FormatType.Auction
      ? offer?.auctionStartPrice
      : offer?.buyItNowPrice;

  const { register, setValue, getValues, watch, handleSubmit, reset } =
    useForm<IStageOptimisationFormFields>({
      defaultValues: {
        newTitle: "",
        newPrice: "",
        newFulfillmentPolicyId: "",
        newPaymentPolicyId: "",
        newReturnPolicyId: "",
        newLocationKey: "",
        applyImageRules: false,
        removeBackgroundFirstImage: false,
        applyCategoryDefaults: false,
      },
    });

  // Prefill with the staged values when re-editing, falling back to the current
  // offer values so the form always shows the full state that would be relisted.
  useEffect(() => {
    if (!open || !part) return;

    reset({
      newTitle: optimisation?.newTitle ?? offer?.title ?? "",
      newPrice: (optimisation?.newPrice ?? currentPrice)?.toString() ?? "",
      newFulfillmentPolicyId: optimisation?.newFulfillmentPolicyId ?? offer?.fulfillmentPolicyId ?? "",
      newPaymentPolicyId: optimisation?.newPaymentPolicyId ?? offer?.paymentPolicyId ?? "",
      newReturnPolicyId: optimisation?.newReturnPolicyId ?? offer?.returnPolicyId ?? "",
      newLocationKey: optimisation?.newLocationKey ?? offer?.locationKey ?? "",
      applyImageRules: optimisation?.applyImageRules ?? false,
      removeBackgroundFirstImage: optimisation?.removeBackgroundFirstImage ?? false,
      applyCategoryDefaults: optimisation?.applyCategoryDefaults ?? false,
    });
    setTitleEditorKey(0);
  }, [open, part, fullPart?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: IStageOptimisationFormFields) => {
    if (!part) return;

    // Only stage values that differ from the current offer - null means
    // "leave unchanged" when the listing is relisted.
    const title = data.newTitle?.trim();
    const price = parseFloat(data.newPrice);

    const item: IStageListingOptimisationItem = {
      partDataId: part.id,
      newTitle: title && title !== offer?.title ? title : null,
      newPrice: !isNaN(price) && price > 0 && price !== currentPrice ? price : null,
      newFulfillmentPolicyId:
        data.newFulfillmentPolicyId && data.newFulfillmentPolicyId !== offer?.fulfillmentPolicyId
          ? data.newFulfillmentPolicyId
          : null,
      newPaymentPolicyId:
        data.newPaymentPolicyId && data.newPaymentPolicyId !== offer?.paymentPolicyId
          ? data.newPaymentPolicyId
          : null,
      newReturnPolicyId:
        data.newReturnPolicyId && data.newReturnPolicyId !== offer?.returnPolicyId
          ? data.newReturnPolicyId
          : null,
      newLocationKey:
        data.newLocationKey && data.newLocationKey !== offer?.locationKey
          ? data.newLocationKey
          : null,
      applyImageRules: data.applyImageRules,
      removeBackgroundFirstImage: data.removeBackgroundFirstImage,
      applyCategoryDefaults: data.applyCategoryDefaults,
    };

    const hasChanges =
      item.newTitle != null ||
      item.newPrice != null ||
      item.newFulfillmentPolicyId != null ||
      item.newPaymentPolicyId != null ||
      item.newReturnPolicyId != null ||
      item.newLocationKey != null ||
      item.applyImageRules === true ||
      item.removeBackgroundFirstImage === true ||
      item.applyCategoryDefaults === true;

    if (!hasChanges && !optimisation) {
      addNotification({ primaryText: "No changes to stage", variant: "warn" });
      return;
    }

    onSubmit(item);
  };

  return (
    <Modal open={open} setOpen={handleClose} width="max-w-5xl">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-4">
          <h2 className="text-lg font-medium leading-6 text-gray-900">Stage Listing Changes</h2>
          <p className="text-sm text-gray-600">
            These changes are saved against the listing but not sent to eBay. They are applied
            when the listing is sent to be relisted.
          </p>

          {offer && (
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <span className="font-medium text-gray-700">Currently live:</span>{" "}
              {offer.title} — {PriceFormat(currentPrice)}
            </div>
          )}

          <div className="rounded-lg border border-sky-200/70 bg-sky-50/30 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                Title (staged)
              </p>
              {fullPart && (
                <button
                  type="button"
                  onClick={() => {
                    const wilmaTitle = (computeEbayListingTitle(fullPart, null, true) ?? "").trim();
                    if (!wilmaTitle) return;
                    setValue("newTitle", wilmaTitle);
                    setTitleEditorKey((previous) => previous + 1);
                  }}
                  className="rounded-md border border-sky-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 transition hover:bg-sky-50"
                >
                  Use Wilma title
                </button>
              )}
            </div>
            {detailsLoading && !partDetails ? (
              <div className="py-3">
                <LoadingPulse />
              </div>
            ) : fullPart ? (
              <EbayListingTitle
                key={`${fullPart.id}-${optimisation?.updated ?? optimisation?.created ?? "new"}-${titleEditorKey}`}
                part={fullPart}
                register={register}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                registerName={"newTitle"}
                persistTitle={false}
                editDefault
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberInput label={"Price"} register={register} registerName={"newPrice"} min={0} />
            <SelectInput label={"Location of Part"} register={register} registerName={"newLocationKey"} options={locations.data?.data.locations || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Shipping Policy"} register={register} registerName={"newFulfillmentPolicyId"} options={policies.data?.data.fulfilmentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Payment Policy"} register={register} registerName={"newPaymentPolicyId"} options={policies.data?.data.paymentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Returns Policy"} register={register} registerName={"newReturnPolicyId"} options={policies.data?.data.returnPolicies || []} setValue={setValue} watch={watch} />
          </div>

          {/* Relist-time actions - applied server-side just before the listing republishes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-md bg-gray-50 px-3 py-2">
            <CheckboxInput
              label={"Apply image rules on relist"}
              register={register}
              registerName={"applyImageRules"}
            />
            <CheckboxInput
              label={"Remove background on 1st image on relist"}
              register={register}
              registerName={"removeBackgroundFirstImage"}
            />
            <CheckboxInput
              label={"Apply category defaults on relist"}
              register={register}
              registerName={"applyCategoryDefaults"}
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput label={"Cancel"} isSubmit={false} onClick={handleClose} classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"} />
            <ButtonInput label={"Stage Changes"} isSubmit={true} onClick={undefined} classes={undefined} isLoading={isLoading} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
